// The Help button on Studio and Matchday setup: ask a question in plain words, Claude answers from the Matchlight guide.
// Club admins only (the service checks the admin code). Not on the scorer's phone.

interface Turn { role: 'user' | 'assistant'; content: string }

const SUGGEST: Record<'studio' | 'setup', string[]> = {
  studio: ['How do I add player photos?', 'How do I change the order of sponsors?', 'What size should a promo video be?'],
  setup: ['How do I start a match?', 'The scorer can’t get in. What do I do?', 'How do I end the match?'],
};

const esc = (t: string) => t.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
/** Just enough formatting for short answers: paragraphs, numbered and bulleted lists, bold. Everything is escaped first. */
function format(text: string) {
  const inline = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  const out: string[] = []; let list: 'ol' | 'ul' | null = null;
  const close = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const line of text.split('\n')) {
    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/), ul = line.match(/^\s*[-*•]\s+(.*)$/);
    if (ol || ul) {
      const kind = ol ? 'ol' : 'ul';
      if (list !== kind) { close(); out.push(`<${kind}>`); list = kind; }
      out.push(`<li>${inline((ol ?? ul)![1])}</li>`);
    } else if (line.trim()) { close(); out.push(`<p>${inline(line.replace(/^#+\s*/, ''))}</p>`); }
    else close();
  }
  close();
  return out.join('');
}

const CSS = `
.mlh-btn { position: fixed; right: max(18px, env(safe-area-inset-right)); bottom: max(18px, env(safe-area-inset-bottom)); z-index: 40;
  display: inline-flex; align-items: center; gap: 8px; min-height: 48px; padding: 0 18px 0 14px; border-radius: 999px; cursor: pointer;
  background: var(--gold, #f5b400); color: #15120a; border: 0; font: 700 16px/1 "Saira Semi Condensed", system-ui, sans-serif; box-shadow: 0 8px 24px rgba(0,0,0,.4); }
.mlh-btn svg { width: 22px; height: 22px; }
.mlh-btn:focus-visible, .mlh-panel :focus-visible { outline: 3px solid var(--gold, #f5b400); outline-offset: 2px; }
.mlh-panel { position: fixed; right: max(18px, env(safe-area-inset-right)); bottom: max(18px, env(safe-area-inset-bottom)); z-index: 41;
  width: min(400px, calc(100vw - 36px)); height: min(600px, calc(100vh - 36px)); display: flex; flex-direction: column;
  background: var(--panel, #161b22); color: var(--text, #eef2f6); border: 1px solid var(--line, #2f3946); border-radius: 16px; box-shadow: 0 18px 50px rgba(0,0,0,.55);
  font: 500 15px/1.45 "Saira Semi Condensed", system-ui, sans-serif; }
.mlh-panel[hidden] { display: none; }
.mlh-head { display: flex; align-items: center; gap: 10px; padding: 14px 14px 12px 18px; border-bottom: 1px solid var(--line, #2f3946); }
.mlh-head h2 { margin: 0; font: 900 22px/1 "Big Shoulders Display", Impact, sans-serif; text-transform: uppercase; flex: 1; }
.mlh-head button { background: transparent; border: 0; color: var(--muted, #9aa6b5); font-size: 15px; cursor: pointer; padding: 6px 8px; border-radius: 8px; min-height: 0; }
.mlh-head button:hover { color: var(--text, #eef2f6); }
.mlh-log { flex: 1; overflow: auto; padding: 14px 18px; display: flex; flex-direction: column; gap: 12px; }
.mlh-intro { color: var(--muted, #9aa6b5); margin: 0; }
.mlh-sugg { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
.mlh-sugg button { text-align: left; background: var(--raise, #222a35); color: var(--text, #eef2f6); border: 1px solid var(--line, #2f3946); border-radius: 10px; padding: 8px 12px; font: inherit; cursor: pointer; min-height: 0; }
.mlh-sugg button:hover { border-color: var(--gold, #f5b400); }
.mlh-q { align-self: flex-end; max-width: 88%; background: #2a3442; border-radius: 12px 12px 4px 12px; padding: 8px 12px; white-space: pre-wrap; }
.mlh-a { max-width: 100%; }
.mlh-a p { margin: 0 0 8px; } .mlh-a p:last-child { margin: 0; }
.mlh-a ol, .mlh-a ul { margin: 0 0 8px; padding-left: 22px; display: grid; gap: 4px; }
.mlh-a b { color: #fff; }
.mlh-a.wait::after { content: '…'; color: var(--muted, #9aa6b5); }
.mlh-err { color: #ffb4b4; margin: 0; }
.mlh-form { display: flex; gap: 8px; padding: 12px 14px 14px; border-top: 1px solid var(--line, #2f3946); }
.mlh-form textarea { flex: 1; resize: none; height: 46px; max-height: 120px; font: inherit; color: var(--text, #eef2f6); background: #0f1318; border: 1px solid var(--line, #2f3946); border-radius: 10px; padding: 11px 12px; }
.mlh-form button { background: var(--gold, #f5b400); color: #15120a; border: 0; border-radius: 10px; padding: 0 16px; font: 700 15px "Saira Semi Condensed", sans-serif; cursor: pointer; min-height: 46px; }
.mlh-form button:disabled { opacity: .5; cursor: default; }
.mlh-foot { margin: -6px 18px 12px; color: var(--muted, #9aa6b5); font-size: 12px; }
.mlh-lift .mlh-btn, .mlh-lift .mlh-panel { bottom: calc(64px + env(safe-area-inset-bottom)); }   /* clear of Studio's save bar */
.mlh-lift .mlh-panel { height: min(600px, calc(100vh - 84px)); }
@media print { .mlh-btn, .mlh-panel { display: none; } }`;

export function mountHelp(o: { page: 'studio' | 'setup'; venue: string; code: () => string; context: () => string }) {
  if (o.page === 'studio') document.body.classList.add('mlh-lift');
  const style = document.createElement('style'); style.textContent = CSS; document.head.append(style);
  const btn = document.createElement('button');
  btn.className = 'mlh-btn'; btn.type = 'button'; btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.2 9.3a2.9 2.9 0 0 1 5.6 1c0 2-2.8 2.6-2.8 4.2"/><circle cx="12" cy="17.6" r=".6" fill="currentColor"/></svg>Help';
  const panel = document.createElement('section');
  panel.className = 'mlh-panel'; panel.hidden = true; panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-label', 'Matchlight help');
  panel.innerHTML = `<div class="mlh-head"><h2>Help</h2><button type="button" data-new>New question</button><button type="button" data-close aria-label="Close help">Close</button></div>
    <div class="mlh-log" aria-live="polite"></div>
    <form class="mlh-form"><textarea rows="1" placeholder="Ask how to do something…" aria-label="Your question" maxlength="1000"></textarea><button type="submit">Ask</button></form>
    <p class="mlh-foot">Answers come from Claude, an AI, using the Matchlight guide. Check anything important.</p>`;
  document.body.append(btn, panel);

  const log = panel.querySelector('.mlh-log') as HTMLDivElement;
  const form = panel.querySelector('form') as HTMLFormElement, box = form.querySelector('textarea') as HTMLTextAreaElement, ask = form.querySelector('button') as HTMLButtonElement;
  let turns: Turn[] = [], busy = false;

  function intro() {
    log.innerHTML = `<p class="mlh-intro">Ask how to do something in ${o.page === 'studio' ? 'Studio' : 'Matchday setup'}, or on matchday.</p>
      <div class="mlh-sugg">${SUGGEST[o.page].map(q => `<button type="button">${esc(q)}</button>`).join('')}</div>`;
    log.querySelectorAll<HTMLButtonElement>('.mlh-sugg button').forEach(b => b.onclick = () => send(b.textContent ?? ''));
  }
  const open = (yes: boolean) => {
    panel.hidden = !yes; btn.hidden = yes; btn.setAttribute('aria-expanded', String(yes));
    if (yes) { if (!turns.length) intro(); box.focus(); } else btn.focus();
  };
  btn.onclick = () => open(true);
  (panel.querySelector('[data-close]') as HTMLButtonElement).onclick = () => open(false);
  (panel.querySelector('[data-new]') as HTMLButtonElement).onclick = () => { if (busy) return; turns = []; intro(); box.focus(); };
  panel.addEventListener('keydown', e => { if (e.key === 'Escape') open(false); });
  box.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); } });
  form.onsubmit = e => { e.preventDefault(); send(box.value); };

  async function send(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    if (!turns.length) log.innerHTML = '';
    busy = true; ask.disabled = true; box.value = '';
    log.insertAdjacentHTML('beforeend', `<div class="mlh-q">${esc(q)}</div>`);
    const a = document.createElement('div'); a.className = 'mlh-a wait'; log.append(a); log.scrollTop = log.scrollHeight;
    turns.push({ role: 'user', content: q });
    let answer = '';
    try {
      const r = await fetch(`/api/venue/${encodeURIComponent(o.venue)}/help`, {
        method: 'POST', headers: { 'content-type': 'application/json', 'x-admin-code': encodeURIComponent(o.code()) },
        body: JSON.stringify({ page: o.page, context: o.context(), messages: turns }),
      });
      if (!r.ok || !r.body) {
        const msg = (await r.json().catch(() => ({})) as { error?: string }).error;
        throw new Error(r.status === 401 ? 'Sign in with the club’s admin code first.' : msg || 'Help couldn’t answer just now. Try again in a minute.');
      }
      // the answer arrives a few words at a time
      const reader = r.body.getReader(), dec = new TextDecoder(); let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let cut: number;
        while ((cut = buf.indexOf('\n\n')) >= 0) {
          const block = buf.slice(0, cut); buf = buf.slice(cut + 2);
          const data = block.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trim()).join('');
          if (!data) continue;
          let ev: { type?: string; delta?: { type?: string; text?: string }; error?: { message?: string } };
          try { ev = JSON.parse(data); } catch { continue; }
          if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') { answer += ev.delta.text ?? ''; a.innerHTML = format(answer); log.scrollTop = log.scrollHeight; }
          if (ev.type === 'error') throw new Error('Help couldn’t finish that answer. Try asking again.');
        }
      }
      if (!answer.trim()) throw new Error('Help didn’t come back with an answer. Try asking again.');
      turns.push({ role: 'assistant', content: answer });
    } catch (e) {
      turns.pop();
      a.remove();
      log.insertAdjacentHTML('beforeend', `<p class="mlh-err">${esc(e instanceof TypeError ? 'Help couldn’t be reached. Check your connection and try again.' : (e as Error).message)}</p>`);
    } finally {
      a.classList.remove('wait'); busy = false; ask.disabled = false; box.focus(); log.scrollTop = log.scrollHeight;
    }
  }
}
